import {
  provideHttpClient,
  HTTP_INTERCEPTORS,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { ApplicationConfig, inject, isDevMode } from '@angular/core';
import { provideAnalytics, getAnalytics } from '@angular/fire/analytics';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { provideRouter, Router, withViewTransitions } from '@angular/router';
import { InMemoryCache } from '@apollo/client/core';
import { provideApollo } from 'apollo-angular';
import { AuthInterceptorService } from './app/services/auth-interceptor.service';
import { environment } from './environments/environment';
import { HttpLink } from 'apollo-angular/http';
import { routes } from './routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { lotReducer } from './app/store/lots-library/lots.reducer';
import { marketOptionReducer } from './app/store/selected-market/select-market.reducer';
import { SelectedLotQueryEffect } from './app/store/lots-library/selected-lot.effects';
import { FetchTableDataEffect } from './app/store/table-data/table-data.effects';
import { tableDataReducer } from './app/store/table-data/table-data.reducer';
import { FetchUserDataEffect } from './app/store/user/user.effects';
import { userReducer } from './app/store/user/user.reducer';
import { provideServiceWorker } from '@angular/service-worker';
import { NegotiationsEffects } from './app/store/negotiations/negotiations.effects';
import { negotiationsReducer } from './app/store/negotiations/negotiations.reducer';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

export const appConfig: ApplicationConfig = {
  providers: [
    provideApollo(() => {
      const httpLink = inject(HttpLink);

      // Default is `no-cache` for both read paths: every query hits the server.
      // This is intentional for trading data freshness — opt individual queries
      // into caching via `{ fetchPolicy: 'cache-and-network' }` on the watch call
      // (see negotiations.component for an example). Switching the global default
      // is a behavior change: cached emissions arrive before network ones, so
      // subscribers that assume a single emission per query may need updating.
      return {
        link: httpLink.create({ uri: environment.graphQLServer }),
        cache: new InMemoryCache(),
        defaultOptions: {
          watchQuery: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
          },
          query: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
          },
        },
      };
    }),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    provideAnimationsAsync(),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideMessaging(() => getMessaging()),
    provideAnalytics(() => getAnalytics()),
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: { color: 'primary' },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    provideRouter(routes, withViewTransitions()),
    Router,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideStore(),
    provideState('user', userReducer),
    provideState('market', marketOptionReducer),
    provideState('activeLot', lotReducer),
    provideState('lotTableData', tableDataReducer),
    provideState('negotiations', negotiationsReducer),
    provideEffects([
      SelectedLotQueryEffect,
      FetchTableDataEffect,
      FetchUserDataEffect,
      NegotiationsEffects,
    ]),
    ...(isDevMode() ? [provideStoreDevtools({ maxAge: 10 })] : []),
  ],
};
